import { z } from 'zod';
import axios from 'axios';
import type { CreateCustomFieldsDTO, CreateTemplateCustomFieldsDTO, UpdateCustomFieldDTO } from '@proposify/document-service-api-schema';
import { documentServiceClient } from '@proposify/document-service-api-schema';
import { trackError } from '@proposify/frontend-observability';
import { NetworkValidationError } from 'services/errors/NetworkValidationError';
import { DeletedCustomFieldError } from 'services/errors/DeletedCustomFieldError';
import { getDocumentServicePublicClientConfig, getDocumentServiceSchemaClient } from 'services/api/client';
import type { DocServiceCustomFieldsRepository } from './DocServiceCustomFieldsRepository';
import {
  assignedCustomFieldDtoSchema,
  PermissionsDTO,
  type AssignedCustomField,
  type AssignedCustomFieldDto,
  type CustomField,
  type UserPermissionsDTO,
} from './schemas';

export class ApiDocServiceCustomFieldsRepository implements DocServiceCustomFieldsRepository {
  public async getAssignedCustomFields(contentId: string, isTemplate = false): Promise<AssignedCustomFieldDto[]> {
    let data;
    try {
      data = isTemplate
        ? await documentServiceClient.templatePublicGetCustomFields(contentId, getDocumentServicePublicClientConfig())
        : await documentServiceClient.documentsPublicGetAllCustomFields(contentId, getDocumentServicePublicClientConfig());
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        errorFrom: 'editor-remote',
        contentId,
      });
      throw new Error('Error fetching custom fields');
    }

    try {
      return z.array(assignedCustomFieldDtoSchema).parse(data);
    } catch (e) {
      const validationError = new NetworkValidationError('getCustomFields response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });
      trackError(validationError, {
        errorFrom: 'editor-remote',
        contentId,
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });
      throw new Error('Error fetching custom fields');
    }
  }

  public async assignCustomField(contentId: string, field: CustomField, isTemplate = false): Promise<AssignedCustomFieldDto[]> {
    let data;
    try {
      const customFieldToAdd = {
        id: field.id,
        name: field.name,
        friendlyName: field.friendlyName,
        type: field.fieldType,
        options: field.options,
        placeholder: field.placeholder,
        isRequired: false,
        path: null,
      };

      if (!isTemplate) customFieldToAdd['value'] = null;

      const customFieldData = { customFields: [customFieldToAdd] };

      data = isTemplate
        ? await documentServiceClient.templateCreateCustomField(
            contentId,
            customFieldData as CreateTemplateCustomFieldsDTO,
            getDocumentServiceSchemaClient()
          )
        : await documentServiceClient.documentsCreateCustomFields(
            contentId,
            customFieldData as CreateCustomFieldsDTO,
            getDocumentServiceSchemaClient()
          );
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 400) {
        const message = (e.response.data as Record<string, unknown>)?.message;
        if (typeof message === 'string' && message.includes('have been deleted')) {
          throw new DeletedCustomFieldError();
        }
      }
      trackError(e instanceof Error ? e : new Error(String(e)), {
        errorFrom: 'editor-remote',
        contentId,
      });
      throw new Error('Error assigning custom field');
    }

    try {
      return z.array(assignedCustomFieldDtoSchema).parse(data);
    } catch (e) {
      const validationError = new NetworkValidationError('assignCustomField response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });
      trackError(validationError, {
        errorFrom: 'editor-remote',
        contentId,
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });
      throw new Error('Error assigning custom field');
    }
  }

  public async updateCustomField(
    contentId: string,
    customFieldId: string,
    type: AssignedCustomField['type'],
    value: unknown
  ): Promise<AssignedCustomFieldDto> {
    let data;
    try {
      data = await documentServiceClient.documentsUpdateCustomFields(
        contentId,
        customFieldId,
        { type, value: value as object } as UpdateCustomFieldDTO,
        getDocumentServiceSchemaClient()
      );
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        errorFrom: 'editor-remote',
        contentId,
        customFieldId,
      });
      throw new Error('Error updating custom field');
    }

    try {
      return assignedCustomFieldDtoSchema.parse(data);
    } catch (e) {
      const validationError = new NetworkValidationError('updateCustomField response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });
      trackError(validationError, {
        errorFrom: 'editor-remote',
        contentId,
        customFieldId,
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });
      throw new Error('Error updating custom field');
    }
  }

  public async unassignCustomField(contentId: string, customFieldId: string, level: 'document' | 'template'): Promise<void> {
    try {
      if (level === 'template') {
        await documentServiceClient.templateDeleteCustomField(contentId, customFieldId, getDocumentServiceSchemaClient());
      } else {
        await documentServiceClient.documentsDeleteCustomField(contentId, customFieldId, getDocumentServiceSchemaClient());
      }
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        errorFrom: 'editor-remote',
        contentId,
        customFieldId,
      });
      throw new Error('Error unassigning custom field');
    }
  }

  public async getUserPermissions(): Promise<UserPermissionsDTO> {
    try {
      const response = await documentServiceClient.userGetUserPermissions(getDocumentServiceSchemaClient());
      return PermissionsDTO.parse(response);
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        endpoint: 'getUserPermissions',
        errorFrom: 'editor-remote',
      });
      throw new Error('Error fetching user permissions');
    }
  }
}
